import React, {useEffect, useState} from 'react';
import {AforoCelda, CLGDetalleFila} from "./SeccionCLGDatosDetalle";
import {SubmitHandler, useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {EntityWithIdAndDescriptionFields, EntityWithIdFields} from "../../../../../types";
import {OperacionCLGDetalleFields} from "../types/fields";
import toast from "react-hot-toast";
import {AsyncSelect} from "../../../../../components/forms";
import {HttpCacheOperacion} from "../../../api/httpCacheOperacion";
import {Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import {numberFormatter} from "../../../../../utils/formatters/numberFormatter";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import {TextField} from "src/lib/itaptec/components_forms/src";
import Icon from "../../../../../@core/components/icon";

const schemaActualizarAforo = z.object({
  [EntityWithIdFields.Id]: z.number(),
  [OperacionCLGDetalleFields.IdCabecera]: z.number().nullable(),
  [OperacionCLGDetalleFields.IdOperacion]: z.number(),
  [OperacionCLGDetalleFields.CodEspecie]: z.number(),
  [OperacionCLGDetalleFields.Especie]: z.string(),
  [OperacionCLGDetalleFields.CodSeccion]: z.number(),

  [OperacionCLGDetalleFields.Aforo]: z.string().min(1, 'Aforo requerido')
    .or(z.number()),

  //[OperacionCLGDetalleFields.Aforo]: z.string(),

  // [OperacionCLGDetalleFields.Aforo]: z.number().min(0, { message: "Aforo debe ser mayor o igual a 0" }).max(100, { message: "Aforo debe ser menor o igual a 100" })
  // .or(z.string().transform(parseFloat).refine(value => Number.isFinite(value) && value >= 0 && value <= 100, {
  //     message: "Aforo debe ser un numero entre 0 y 100",
  // })),
})

export type FormActualizarAforo = z.infer<typeof schemaActualizarAforo>

interface AforoFieldProps {
  fila: CLGDetalleFila,
  onUpdateAforo: (data: FormActualizarAforo) => void,
  aforo: AforoCelda,
  permiteEditar: boolean,
  iniciaEditando?: boolean,
}

const AforoField = React.forwardRef(function AforoField({
                                                          fila,
                                                          onUpdateAforo,
                                                          aforo,
                                                          permiteEditar,
                                                          iniciaEditando
                                                        }: AforoFieldProps, ref) {
  const [editting, setEditting] = useState<boolean>(!!iniciaEditando);
  const [submitting, setSubmitting] = useState(true);
  const [freeChoice, setFreeChoice] = useState(false)


  const {control, handleSubmit, reset} = useForm<FormActualizarAforo>({
    resolver: zodResolver(schemaActualizarAforo),
    defaultValues: {
      [EntityWithIdFields.Id]: aforo[EntityWithIdFields.Id],
      [OperacionCLGDetalleFields.IdCabecera]: fila[OperacionCLGDetalleFields.IdCabecera],
      [OperacionCLGDetalleFields.IdOperacion]: fila[OperacionCLGDetalleFields.IdOperacion],
      [OperacionCLGDetalleFields.CodEspecie]: aforo[OperacionCLGDetalleFields.CodEspecie],
      [OperacionCLGDetalleFields.CodSeccion]: aforo[OperacionCLGDetalleFields.CodSeccion],
      [OperacionCLGDetalleFields.Aforo]: aforo[OperacionCLGDetalleFields.Aforo],
      [OperacionCLGDetalleFields.Especie]: aforo[OperacionCLGDetalleFields.Especie],
    }
  });
  const watchAforo = useWatch({control, name: OperacionCLGDetalleFields.Aforo});


  const onSubmit: SubmitHandler<FormActualizarAforo> = (data) => {
    const promise = new Promise((resolve, reject) => {
      try {
        onUpdateAforo(data);
        resolve('Actualizado correctamente.');
      } catch (e) {
        console.log(e)
        reject(e);
      }
    });

    toast.promise(promise, {
      loading: 'Actualizando aforo.',
      success: 'Actualizado correctamente.',
      error: e => `${e}`
    })
      .then(() => {
        setSubmitting(false);
      })
      .catch(() => {
        setSubmitting(false);
      });

    setSubmitting(true);
  };

  React.useEffect(() => {
    if (!submitting) {
      setEditting(false);
    }
  }, [submitting]);

  React.useImperativeHandle(ref, () => ({
    confirm: () => handleSubmit(onSubmit)()
  }));


  useEffect(() => {
    if (watchAforo === null) {
      setFreeChoice(true)
    }
  }, [watchAforo])


  return (
    <Box display='flex' justifyContent='flex-start' alignItems='center' gap={5}>
      {
        (permiteEditar && editting) ?
          !freeChoice ?
            <AsyncSelect
              control={control}
              name={OperacionCLGDetalleFields.Aforo}
              label={'Aforo'}
              loadOptions={() => HttpCacheOperacion.getAforoTipos()}
              valueField={EntityWithIdAndDescriptionFields.Description}
              nullOptionLabel={'Otro'}
            />
            :
            <Stack direction={'row'} alignItems={'center'}>
              <TextField
                name={OperacionCLGDetalleFields.Aforo}
                label='Aforo'
                control={control}
                sx={{width: 4 / 5}}
                size='small'
              />
              <Tooltip title='Ver opciones'>
                <IconButton onClick={() => {
                  reset()
                  setFreeChoice(false)
                }}>
                  <Icon icon={'mdi:menu'}/>
                </IconButton>
              </Tooltip>
            </Stack>

          :
          <Typography>
            {
              /^[+-]?\d+(\.\d+)?$/.test(watchAforo.toString()) ? numberFormatter.toStringWithAmount(parseFloat(watchAforo.toString()), '', 0) : watchAforo
            }
          </Typography>
      }
      {
        permiteEditar &&
        (
          editting ?
            <Stack direction={'row'}>
              <Tooltip title='Cancelar'>
                <IconButton onClick={() => {
                  reset()
                  setEditting(false)
                }}>
                  <CancelIcon color='error'/>
                </IconButton>
              </Tooltip>
              <Tooltip title='Confirmar'>
                <IconButton onClick={handleSubmit(onSubmit)}>
                  <CheckCircleIcon color='success'/>
                </IconButton>
              </Tooltip>
            </Stack>
            :
            <Tooltip title='Editar'>
              <IconButton onClick={() => setEditting(true)}>
                <EditIcon color='info'/>
              </IconButton>
            </Tooltip>
        )
      }
    </Box>
  );
})

export default AforoField;